import { type Dispatch, useCallback, useEffect, useReducer, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useAppConfig } from "~/app/config/useAppConfig";
import { isLineLevelCustomerOrderNumberEnabled, isPhaseOut2Enabled } from "~/app/config/featureFlags";
import { useCommerceContext } from "~/app/config/useSession";
import type { PriceReferenceSelection } from "~/domains/priceReference/types";
import { useSessionState } from "~/app/providers/SessionProvider";
import { Button, Stack } from "~/common/mui";
import type { RootState } from "~/app/store";
import * as GABulkUpload from "~/app/analytics/ga/bulkUpload";
import { FccAction, trackFccAction } from "~/features/freeCapacityPush/api/trackFccAction";
import { redirectToUrl, resolveCartPageUrl } from "~/utils/url/urlUtils";
import {
  ordinaryBulkUploadOperations,
  type OrdinaryBulkUploadOperations,
  type OrdinaryBulkUploadPreviewInput,
} from "../api/bulkUploadApi";
import type {
  BulkUploadAddResponse,
  BulkUploadEntryIntentWsDTO,
  BulkUploadPreviewProduct,
  BulkUploadPreviewResponse,
  BulkUploadSuccessWsDTO,
  BulkUploadTemplate,
  BulkUploadTemplateType,
} from "../types/contracts";
import {
  bulkUploadReducer,
  createInitialBulkUploadState,
  type BulkUploadWorkflowAction,
  type BulkUploadWorkflowFailure,
} from "../workflow/bulkUploadReducer";
import { BulkUploadBeforePreview, BulkUploadBeforePreviewActions } from "./BulkUploadBeforePreview";
import { BulkUploadDialogRoot } from "./BulkUploadDialogRoot";
import { BulkUploadLoading } from "./BulkUploadLoading";
import { BulkUploadPreview } from "./BulkUploadPreview";
import { ChoosePriceReferenceForCart } from "~/features/cart/cart/components/Buttons/ChoosePriceReferenceForCart";
import { SwitchProductVersions } from "~/features/cart/cart/components/Buttons/SwitchProductVersions";

export interface BulkUploadDialogOrchestratorProps {
  bulkMaxRows?: string;
  cartActionsDisabled?: boolean;
  cartItemAmount: number;
  customerOrderNumberEnabled: boolean;
  newProductVersionEnabled?: boolean;
  priceReferenceActionEnabled?: boolean;
  priceReferenceSelection?: PriceReferenceSelection;
  onAddAttempt: (override: boolean) => void;
  onAddSuccess: (success: BulkUploadSuccessWsDTO, override: boolean) => void;
  onClose: () => void;
  operations: OrdinaryBulkUploadOperations;
  targetPriceEnabled: boolean;
  webservicesDisabled: boolean;
}

export function BulkUploadDialogOrchestrator({
  bulkMaxRows,
  cartActionsDisabled = false,
  cartItemAmount,
  customerOrderNumberEnabled,
  newProductVersionEnabled = false,
  priceReferenceActionEnabled = false,
  priceReferenceSelection,
  onAddAttempt,
  onAddSuccess,
  onClose,
  operations,
  targetPriceEnabled,
  webservicesDisabled,
}: BulkUploadDialogOrchestratorProps) {
  const { t } = useTranslation();
  const [workflow, dispatch] = useReducer(bulkUploadReducer, undefined, () => createInitialBulkUploadState("ordinary"));
  const [text, setText] = useState("");
  const [products, setProducts] = useState<BulkUploadPreviewProduct[]>([]);
  const [rowErrors, setRowErrors] = useState<BulkUploadAddResponse["rowErrors"]>([]);
  const contentRef = useRef<HTMLDivElement>(null);
  const previewAbortController = useRef<AbortController>();
  const previewRequestId = useRef(0);
  const closed = useRef(false);
  const currentTargetPrices = useRef<Array<number | null>>([]);

  useEffect(
    () => () => {
      closed.current = true;
      previewAbortController.current?.abort();
    },
    [],
  );

  const close = useCallback(() => {
    closed.current = true;
    previewAbortController.current?.abort();
    dispatch({ type: "closed" });
    onClose();
  }, [onClose]);

  const closeBeforePreviewFromCancel = useCallback(() => {
    GABulkUpload.trackClose("Cancel Button");
    close();
  }, [close]);

  const closeFromX = useCallback(() => {
    GABulkUpload.trackClose("X Button");
    close();
  }, [close]);

  const requestPreview = useCallback(
    async (input: OrdinaryBulkUploadPreviewInput) => {
      if (cartActionsDisabled) {
        return;
      }
      previewAbortController.current?.abort();
      const controller = new AbortController();
      const requestId = ++previewRequestId.current;
      previewAbortController.current = controller;
      dispatch({ type: "sourceSelected", source: input.source });
      dispatch({ type: "previewRequested" });
      setRowErrors([]);

      try {
        const response = await operations.preview(input, controller.signal);
        if (controller.signal.aborted || requestId !== previewRequestId.current || closed.current) {
          return;
        }
        if (input.source === "file") {
          GABulkUpload.trackFileUpload(isSuccessfulPreviewResponse(response), getFileType(input.file));
        } else {
          GABulkUpload.trackTextUpload(isSuccessfulPreviewResponse(response));
        }
        handlePreviewResponse(response, {
          dispatch,
          setProducts,
          setRowErrors,
          setText,
        });
      } catch (error) {
        if (controller.signal.aborted || isAbortError(error) || closed.current) {
          return;
        }
        if (input.source === "file") {
          GABulkUpload.trackFileUpload(false, getFileType(input.file));
        } else {
          GABulkUpload.trackTextUpload(false);
        }
        dispatch({
          type: "requestFailed",
          failure: {
            kind: "transport",
          },
        });
      } finally {
        if (previewAbortController.current === controller) {
          previewAbortController.current = undefined;
        }
      }
    },
    [cartActionsDisabled, operations],
  );

  const submit = useCallback(
    async (entries: BulkUploadEntryIntentWsDTO[], override: boolean) => {
      if (cartActionsDisabled || workflow.phase !== "preview") {
        return;
      }
      const { previewContext, trackingUid } = workflow;
      const hasComments = entries.some((entry) => Boolean(entry.comment?.trim()));
      onAddAttempt(override);
      dispatch({ type: "addRequested" });

      try {
        const response = await operations.submit({
          override,
          request: {
            entries,
            previewContext,
            ...(trackingUid ? { trackingUid } : {}),
          },
        });
        if (hasComments) {
          GABulkUpload.trackCommentPosted(response.outcome === "SUCCESS");
        }
        if (closed.current) {
          return;
        }
        if (response.outcome === "SUCCESS" && response.success) {
          dispatch({ type: "addSucceeded", success: response.success });
          onAddSuccess(response.success, override);
          return;
        }
        if (response.outcome === "CONTEXT_CHANGED") {
          setProducts([]);
          setRowErrors([]);
          setText("");
          dispatch({ type: "contextChanged" });
          return;
        }

        if (response.products.length > 0) {
          setProducts(response.products);
        }
        setRowErrors(response.rowErrors);
        dispatch({
          type: "requestFailed",
          failure: responseFailure(response),
        });
      } catch {
        if (hasComments) {
          GABulkUpload.trackCommentPosted(false);
        }
        if (!closed.current) {
          dispatch({
            type: "requestFailed",
            failure: {
              kind: "transport",
            },
          });
        }
      }
    },
    [cartActionsDisabled, onAddAttempt, onAddSuccess, operations, workflow],
  );

  const downloadTemplate = useCallback(
    async (type: BulkUploadTemplateType) => {
      try {
        saveTemplate(await operations.downloadTemplate(type));
      } catch {
        // Template failures do not mutate the upload workflow or its tracking identity.
      }
    },
    [operations],
  );

  const isPreviewPending = workflow.phase === "loading" && workflow.operation === "preview";
  const isAddPending = workflow.phase === "loading" && workflow.operation === "add";
  const isAddCompleting = workflow.phase === "complete";
  const showPreview = products.length > 0 && workflow.phase === "preview";
  const showPriceReferenceAction =
    showPreview && priceReferenceActionEnabled && Boolean(priceReferenceSelection?.options.length);
  const showProductVersionAction = showPreview && newProductVersionEnabled && !isPhaseOut2Enabled();
  const updateAllPriceReferences = useCallback(
    (priceReference: string) => {
      const affectedProducts = products.flatMap((product, index) =>
        product.onSale ? [] : [toAnalyticsProduct(product, currentTargetPrices.current[index])],
      );
      let successful = true;
      try {
        setProducts((current) =>
          current.map((product, index) =>
            product.onSale
              ? product
              : {
                  ...product,
                  priceReference,
                  ...(index < currentTargetPrices.current.length
                    ? { targetPrice: currentTargetPrices.current[index] }
                    : {}),
                },
          ),
        );
        setRowErrors([]);
      } catch {
        successful = false;
      }
      GABulkUpload.trackPriceReferenceSelectionForAllSave(affectedProducts, successful, priceReference);
    },
    [products],
  );
  const switchAllProductVersions = useCallback(() => {
    const affectedProducts = products.flatMap((product, index) =>
      product.succeedingProductDesignation ? [toAnalyticsProduct(product, currentTargetPrices.current[index])] : [],
    );
    let successful = true;
    try {
      setProducts((current) =>
        current.map((product) =>
          product.succeedingProductDesignation
            ? {
                ...product,
                productId: product.succeedingProductDesignation,
                succeedingProductDesignation: "",
              }
            : product,
        ),
      );
      setRowErrors([]);
    } catch {
      successful = false;
    }
    GABulkUpload.trackSwitchProductVersionForAllSave(affectedProducts, successful);
  }, [products]);
  return (
    <BulkUploadDialogRoot
      actions={
        workflow.phase === "beforePreview" ? (
          <BulkUploadBeforePreviewActions
            disabled={webservicesDisabled || cartActionsDisabled}
            onCancel={closeBeforePreviewFromCancel}
            onTextSubmit={() => void requestPreview({ source: "text", csvInputString: text })}
            text={text}
          />
        ) : isPreviewPending || isAddPending || isAddCompleting ? (
          <Button data-testid="bulk-upload-btn-cancel" disabled variant="text" onClick={closeBeforePreviewFromCancel}>
            {t("bulkUpload.buttons.cancel")}
          </Button>
        ) : undefined
      }
      contentRef={contentRef}
      headerActions={
        showPriceReferenceAction || showProductVersionAction ? (
          <Stack
            sx={{
              flexDirection: "row",
              gap: "8px",
              alignItems: "center",
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}
          >
            {showPriceReferenceAction && (
              <ChoosePriceReferenceForCart
                onInitiate={() =>
                  GABulkUpload.trackPriceReferenceSelectionForAllInitiated(
                    products.flatMap((product, index) =>
                      product.onSale ? [] : [toAnalyticsProduct(product, currentTargetPrices.current[index])],
                    ),
                  )
                }
                priceReferenceSelection={priceReferenceSelection}
                updatePriceReference={updateAllPriceReferences}
              />
            )}
            {showProductVersionAction && (
              <SwitchProductVersions
                newProductVersionEnabled
                onInitiate={() =>
                  GABulkUpload.trackSwitchProductVersionForAllInitiated(
                    products.flatMap((product, index) =>
                      product.succeedingProductDesignation
                        ? [toAnalyticsProduct(product, currentTargetPrices.current[index])]
                        : [],
                    ),
                  )
                }
                switchProductVersion={switchAllProductVersions}
              />
            )}
          </Stack>
        ) : undefined
      }
      onClose={closeFromX}
      title={t("cartPage.links.bulkAdd")}
    >
      {showPreview ? (
        <BulkUploadPreview
          bulkMaxRows={bulkMaxRows}
          customerOrderNumberEnabled={customerOrderNumberEnabled}
          disabled={webservicesDisabled || cartActionsDisabled}
          failure={workflow.phase === "preview" ? workflow.failure : undefined}
          priceReferenceSelection={priceReferenceSelection}
          onCancel={closeBeforePreviewFromCancel}
          onLastRowRemoved={() => {
            setProducts([]);
            setRowErrors([]);
            dispatch({ type: "lastRowRemoved" });
          }}
          onOverrideConfirmationClose={() => dispatch({ type: "overrideConfirmationClosed" })}
          onOverrideConfirmationOpen={() => dispatch({ type: "overrideConfirmationOpened" })}
          onSaleOnlyChange={(value) => dispatch({ type: "saleOnlyChanged", value })}
          onTargetPricesChange={(targetPrices) => {
            currentTargetPrices.current = targetPrices;
          }}
          onSubmit={submit}
          overrideConfirmationOpen={workflow.phase === "preview" ? workflow.overrideConfirmationOpen : false}
          pending={isAddPending}
          products={products}
          rowErrors={rowErrors}
          saleOnly={workflow.phase === "preview" || isAddPending ? workflow.saleOnly : false}
          scrollContainerRef={contentRef}
          showOverrideAction={cartItemAmount > 0}
          targetPriceEnabled={targetPriceEnabled}
          terminalActionsDisabled={cartActionsDisabled}
        />
      ) : isPreviewPending ? (
        <BulkUploadLoading operation="preview" />
      ) : isAddPending ? (
        <BulkUploadLoading operation="add" />
      ) : workflow.phase === "beforePreview" ? (
        <BulkUploadBeforePreview
          bulkMaxRows={bulkMaxRows}
          disabled={webservicesDisabled || cartActionsDisabled}
          onDownloadTemplate={(type) => void downloadTemplate(type)}
          onFileSelected={(file) => void requestPreview({ source: "file", file })}
          onTextChange={setText}
          state={workflow}
          text={text}
        />
      ) : workflow.phase === "complete" ? (
        <BulkUploadLoading operation="add" />
      ) : null}
    </BulkUploadDialogRoot>
  );
}

interface BulkAddToCartDialogProps {
  onClose: () => void;
}

const BulkAddToCartDialog = ({ onClose }: BulkAddToCartDialogProps) => {
  const { cartActionsDisabled } = useSessionState();
  const configuration = useAppConfig()?.configuration;
  const commerceContext = useCommerceContext();
  const cartItemAmount = useSelector((state: RootState) => state.minicart.count);

  const handleSuccess = useCallback(
    (success: BulkUploadSuccessWsDTO, override: boolean) => {
      if (override) {
        GABulkUpload.overrideCart(success.productAmount);
      } else {
        GABulkUpload.addToCart(success.productAmount);
      }
      const params = new URLSearchParams({
        bulkSuccess: success.productAmount.toString(),
        quantityAdjusted: success.quantityAdjusted.toString(),
        ...(override ? { override: "true" } : {}),
      });
      onClose();
      redirectToUrl(`${resolveCartPageUrl()}?${params.toString()}`);
    },
    [onClose],
  );

  return cartActionsDisabled ? null : (
    <BulkUploadDialogOrchestrator
      bulkMaxRows={configuration?.bulkMaxRows}
      cartActionsDisabled={cartActionsDisabled}
      cartItemAmount={cartItemAmount}
      customerOrderNumberEnabled={isLineLevelCustomerOrderNumberEnabled()}
      newProductVersionEnabled={commerceContext?.newProductVersionEnabled ?? false}
      priceReferenceActionEnabled={commerceContext?.showPriceRefOnCartButton ?? false}
      priceReferenceSelection={commerceContext?.priceReferenceSelection}
      onAddAttempt={() => trackFccAction(FccAction.BulkEntry)}
      onAddSuccess={handleSuccess}
      onClose={onClose}
      operations={ordinaryBulkUploadOperations}
      targetPriceEnabled={commerceContext?.targetPriceEnabled ?? false}
      webservicesDisabled={configuration?.disableAllWS ?? false}
    />
  );
};

export default BulkAddToCartDialog;

function handlePreviewResponse(
  response: BulkUploadPreviewResponse,
  handlers: {
    dispatch: Dispatch<BulkUploadWorkflowAction>;
    setProducts: (products: BulkUploadPreviewProduct[]) => void;
    setRowErrors: (rowErrors: BulkUploadPreviewResponse["rowErrors"]) => void;
    setText: (text: string) => void;
  },
) {
  if (isSuccessfulPreviewResponse(response)) {
    handlers.setProducts(response.products);
    handlers.setRowErrors(response.rowErrors);
    handlers.dispatch({
      type: "previewReceived",
      previewContext: response.previewContext,
      ...(response.trackingUid ? { trackingUid: response.trackingUid } : {}),
    });
    return;
  }
  if (response.outcome === "CONTEXT_CHANGED") {
    handlers.setProducts([]);
    handlers.setRowErrors([]);
    handlers.setText("");
    handlers.dispatch({ type: "contextChanged" });
    return;
  }
  handlers.setProducts([]);
  handlers.setRowErrors(response.rowErrors);
  handlers.dispatch({
    type: "requestFailed",
    failure: responseFailure(response),
  });
}

function isSuccessfulPreviewResponse(response: BulkUploadPreviewResponse): response is BulkUploadPreviewResponse & {
  previewContext: NonNullable<BulkUploadPreviewResponse["previewContext"]>;
} {
  return response.outcome === "PREVIEW" && Boolean(response.previewContext) && response.products.length > 0;
}

function getFileType(file: File): string {
  return file.name.match(/\.([^.]+)$/)?.[1]?.toLowerCase() ?? "unknown";
}

function toAnalyticsProduct(
  product: BulkUploadPreviewProduct,
  currentTargetPrice?: number | null,
): GABulkUpload.BulkUploadAnalyticsProduct {
  const targetPrice = currentTargetPrice === undefined ? product.targetPrice : currentTargetPrice;
  return {
    id: product.productId,
    quantity: product.quantity,
    date: product.requestDeliveryDate ?? "",
    price_reference: product.priceReference ?? "",
    ...(targetPrice != null ? { target_price: targetPrice } : {}),
  };
}

function responseFailure(response: BulkUploadPreviewResponse | BulkUploadAddResponse): BulkUploadWorkflowFailure {
  const kind: BulkUploadWorkflowFailure["kind"] =
    response.outcome === "VALIDATION_FAILED" || response.outcome === "INVALID_REQUEST"
      ? "validation"
      : response.outcome === "BUSINESS_ERROR"
      ? "business"
      : "upload";
  return {
    kind,
    ...(response.globalError ? { error: response.globalError } : {}),
    ...(response.rowErrors.length > 0 ? { rowErrors: response.rowErrors } : {}),
  };
}

function saveTemplate(template: BulkUploadTemplate) {
  const url = URL.createObjectURL(template.blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = template.fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}
